/* eslint-disable @typescript-eslint/no-explicit-any -- DIRECT-239681 */

import { useCallback, useEffect, useRef } from 'react';
import { AsyncFlowError, useAsyncFlow, useFlow, skipToken, type SkipToken } from '@adv-frontend/modules-flow';
import { QueryStatus } from '@reduxjs/toolkit/query/react';

import { createQueryHook } from '../createQueryHook';
import type { AsyncQueryFlow } from './buildEndpointQueryFlow';
import type { AnyQueryDefinition, UseQueryResult } from './types';

/**
 * Дополнительные параметры для UseQuery хука.
 */
export type AdditionalUseQueryOptions = {
    /**
     * Количество секунд, в течении которых в сторе будет хранится подписка на данные,
     * полученные при ожидании отрисовки Suspense. При монтировании компонента, либо
     * по окончании указанного времени, подписка будет удалена.
     *
     * @default 60
     */
    keepPrefetchedDataFor?: number;
};

type UseQuery = {
    (queryFlow: SkipToken, options?: AdditionalUseQueryOptions): UseQueryResult<AnyQueryDefinition>;
    <Definition extends AnyQueryDefinition>(
        queryFlow: AsyncQueryFlow<Definition>,
        options?: AdditionalUseQueryOptions
    ): UseQueryResult<Definition>;
    <Definition extends AnyQueryDefinition>(
        queryFlow: AsyncQueryFlow<Definition> | SkipToken,
        options?: AdditionalUseQueryOptions
    ): UseQueryResult<Definition>;
    parallel: <Definition extends AnyQueryDefinition>(
        queryFlow: AsyncQueryFlow<Definition> | SkipToken,
        options?: AdditionalUseQueryOptions
    ) => () => UseQueryResult<Definition>;
};

/**
 * Хук подписки на `AsyncQueryFlow`.
 *
 * @example
 * ```ts
 * import { useQuery } from '@adv-frontend/modules-query';
 *
 * const result = useQuery(someQueryFlow());
 * ```
 *
 * @param queryFlow Поток запроса, либо `skipToken` из `@adv-frontend/modules-flow` для пропуска подписки.
 * @param options Дополнительные опции подписки.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- перегрузки нельзя описать прямо в createQueryHook
export const useQuery = createQueryHook(function useQuery<Definition extends AnyQueryDefinition>(
    queryFlow: AsyncQueryFlow<Definition> | SkipToken,
    options?: AdditionalUseQueryOptions
): UseQueryResult<Definition> {
    const asyncResult = useAsyncFlow(queryFlow, {
        keepRenderSubscriptionFor: options?.keepPrefetchedDataFor ? options.keepPrefetchedDataFor * 1000 : undefined,
    });
    const currentRequest = useFlow(queryFlow === skipToken ? skipToken : queryFlow.currentRequest);

    // `useAsyncFlow` отдает ошибку завернутой в `AsyncFlowError`, чтобы ручной `throw result.error`
    // донес до Error Boundary поток и снапшот. У `useQuery` контракт легаси: наружу уходит
    // исходная ошибка — по ней потребители делают `isRequestError` и читают `response`.
    const unwrappedError = asyncResult?.isError ? unwrapError(asyncResult.error) : undefined;

    // lastErrorRef: запоминает последнюю ошибку в рамках текущего flow — при refetch после
    // ошибки возвращаем её в `error` с pending-флагами вместо ухода в Suspense (как RTK Query).
    // lastDataRef: запоминает последние успешные данные для возврата в `data` при
    // skipToken/uninitialized (RTK Query-like `lastResult.data`). Без этого controlled-
    // компоненты теряют выбранные значения при переходах состояний.
    const lastErrorRef = useRef<{ flow: unknown; error: unknown } | null>(null);
    const lastDataRef = useRef<unknown>(undefined);

    useEffect(() => {
        if (lastErrorRef.current !== null && lastErrorRef.current.flow !== queryFlow) {
            lastErrorRef.current = null;
        }
        if (asyncResult?.isError) {
            lastErrorRef.current = { flow: queryFlow, error: unwrappedError };
        } else if (asyncResult?.isReady) {
            lastErrorRef.current = null;
        }

        if (queryFlow !== skipToken) {
            const data = queryFlow.getSnapshot().data;
            if (data !== undefined) {
                lastDataRef.current = data;
            }
        }
    });

    // Ref'ы обновляются в useEffect'е предыдущего render'а — значение в них валидно только
    // для того flow, к которому оно было записано. Фильтруем по текущему queryFlow.
    const effectiveLastError =
        lastErrorRef.current !== null && lastErrorRef.current.flow === queryFlow ? lastErrorRef.current : null;

    const refetch = useCallback((): Promise<void> => {
        if (queryFlow === skipToken) {
            return Promise.resolve();
        }
        queryFlow.refetch();
        return queryFlow.asPromise().then(
            () => undefined,
            () => undefined
        );
    }, [queryFlow]);

    if (!asyncResult || queryFlow === skipToken) {
        return {
            ...STATUS_FLAGS[QueryStatus.uninitialized],
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
            data: lastDataRef.current as any,
            currentData: undefined,
            error: undefined,
            originalArgs: undefined,
            refetch,
        };
    }

    // currentRequest === null означает, что в state нет requestId (например,
    // данные положены через upsertQueryData или гидрированы без поля requestId).
    // В этом случае не возвращаем uninitialized — если в asyncResult есть данные,
    // отдаём их с fulfilled-флагами, чтобы потребители (form-kit и пр.) не получали
    // undefined на первом рендере и не ловили uncontrolled→controlled.
    const requestMeta = {
        originalArgs: queryFlow.originalQueryArg,
        endpointName: queryFlow.endpointName,
        requestId: currentRequest?.requestId ?? '',
        startedTimeStamp: currentRequest?.startedTimestamp ?? 0,
        fulfilledTimeStamp: currentRequest?.fulfilledTimestamp ?? undefined,
        invalidatedBy: currentRequest?.invalidatedBy,
        refetch,
    };

    if (asyncResult.isError) {
        const data = queryFlow.getSnapshot().data;
        return {
            ...requestMeta,
            ...STATUS_FLAGS[QueryStatus.rejected],
            data,
            currentData: data,
            error: unwrappedError,
        };
    }

    // Pending после error без данных — показываем старую ошибку с
    // isFetching:true, чтобы не триггерить Suspense.
    if (asyncResult.isLoading && effectiveLastError !== null) {
        return {
            ...requestMeta,
            status: QueryStatus.pending,
            isUninitialized: false,
            isFetching: true,
            isSuccess: false,
            isError: false,
            data: undefined,
            currentData: undefined,
            error: effectiveLastError.error,
        };
    }

    // Pending с успешными данными из прошлого запроса (background refetch) —
    // `error` может содержать предыдущую ошибку, пока не придёт новый ответ.
    if (!asyncResult.isLoading && asyncResult.isPending) {
        return {
            ...requestMeta,
            ...STATUS_FLAGS[QueryStatus.pending],
            data: asyncResult(),
            currentData: asyncResult.isStale ? undefined : asyncResult.current(),
            error: effectiveLastError !== null ? effectiveLastError.error : undefined,
        };
    }

    return {
        ...requestMeta,
        ...STATUS_FLAGS[QueryStatus.fulfilled],
        data: asyncResult(),
        currentData: asyncResult.current(),
        error: undefined,
    };
}) as UseQuery;

/** Снимает обертку `AsyncFlowError`: исходная ошибка лежит в `cause`. Не-обертку отдает как есть. */
function unwrapError(error: unknown): unknown {
    return error instanceof AsyncFlowError ? error.cause : error;
}

const STATUS_FLAGS = {
    [QueryStatus.uninitialized]: {
        status: QueryStatus.uninitialized,
        isUninitialized: true,
        isFetching: false,
        isSuccess: false,
        isError: false,
    },
    [QueryStatus.pending]: {
        status: QueryStatus.pending,
        isUninitialized: false,
        isFetching: true,
        isSuccess: true,
        isError: false,
    },
    [QueryStatus.fulfilled]: {
        status: QueryStatus.fulfilled,
        isUninitialized: false,
        isFetching: false,
        isSuccess: true,
        isError: false,
    },
    [QueryStatus.rejected]: {
        status: QueryStatus.rejected,
        isUninitialized: false,
        isFetching: false,
        isSuccess: false,
        isError: true,
    },
} as const;
